import {
	Box,
	Chip,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	Stack,
	Menu,
	MenuItem,
	Typography,
} from "@mui/material";
import {
	Adjust,
	Build,
	Close,
	ContentCopy,
	Download,
	ErrorOutline,
	FolderOpen,
	Map as MapIcon,
	ScatterPlot,
	TableRows,
} from "@mui/icons-material";
import { usePostSamplingIssuesViewModel } from "./PostSamplingIssues.vm";
import { PortalTooltip } from "../../helpers";
import { OpsMap } from "../../helpers/OpsMap";
import { SampleDetailsDialog } from "./SampleDetailsDialog";
import { CoreDetailsDialog } from "./CoreDetailsDialog";
import { CsvDryRunDialog } from "./CsvDryRunDialog";
import type { Mission } from "@rogoag/shared-ts";

type VM = ReturnType<typeof usePostSamplingIssuesViewModel>;

const sectionSx = { bgcolor: "grey.50", borderRadius: 2, p: 2 } as const;

const gridSx = {
	display: "grid",
	gridTemplateColumns: "repeat(3, auto 1fr)",
	columnGap: 2,
	rowGap: 0.5,
	"& > :nth-of-type(6n+3)": { pl: 2 },
	"& > :nth-of-type(6n+5)": { pl: 2 },
} as const;

export function JobDetailPane({ vm }: { vm: VM }) {
	const job = vm.selectedJob!;
	const isFixing = vm.fixingIds.has(job.id);
	const availableAttachments = vm.getAvailableAttachments(job);

	return (
		<Stack spacing={2}>
			{/* Header */}
			<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
				<Box>
					<Stack direction="row" spacing={1} alignItems="center">
						<Typography variant="h5">{job.fieldName || "Unnamed Field"}</Typography>
						<Chip label={job.status} size="small" variant="outlined" />
					</Stack>
					<Typography variant="body2" color="text.secondary">
						{[job.growerName, job.farmName, job.companyName].filter(Boolean).join(" · ")}
					</Typography>
					<Typography variant="caption" color="text.disabled">
						{job.id}
					</Typography>
				</Box>
				{isFixing ? (
					<CircularProgress size={28} />
				) : (
					<Box sx={{ display: "flex", gap: 0.5 }}>
						<PortalTooltip title="Map">
							<IconButton onClick={() => vm.setMapOpen(true)} color="primary">
								<MapIcon />
							</IconButton>
						</PortalTooltip>
						{vm.exeMission && (
							<>
								<PortalTooltip title="CSV Dry-Run">
									<span>
										<IconButton
											onClick={() => vm.setCsvDryRunModalOpen(true)}
											disabled={!vm.csvDryRunData}
											color="primary"
										>
											<TableRows />
										</IconButton>
									</span>
								</PortalTooltip>
								<PortalTooltip title={`Samples (${vm.exeMission.centroids.length})`}>
									<IconButton onClick={() => vm.setDetailModal("samples")} color="primary">
										<Adjust />
									</IconButton>
								</PortalTooltip>
								<PortalTooltip title={`Cores (${vm.exeMission.cores.length})`}>
									<IconButton onClick={() => vm.setDetailModal("cores")} color="primary">
										<ScatterPlot />
									</IconButton>
								</PortalTooltip>
							</>
						)}
						{availableAttachments.length > 0 && (
							<>
								<PortalTooltip title="Download Files">
									<IconButton onClick={(e) => vm.setDownloadAnchor(e.currentTarget)} color="primary">
										<Download />
									</IconButton>
								</PortalTooltip>
								<Menu
									anchorEl={vm.downloadAnchor}
									open={!!vm.downloadAnchor}
									onClose={() => vm.setDownloadAnchor(null)}
								>
									{availableAttachments.map((att) => (
										<MenuItem
											key={att.key}
											onClick={() => {
												vm.downloadAttachment(att, job.id);
												vm.setDownloadAnchor(null);
											}}
										>
											<Download sx={{ mr: 1, fontSize: "1.2rem" }} />
											{att.label}
										</MenuItem>
									))}
								</Menu>
							</>
						)}
						<PortalTooltip title="Browse Core Photos">
							<IconButton
								component="a"
								href={`${import.meta.env.VITE_FILE_SERVER}/files/${job.id}/`}
								target="_blank"
								rel="noopener noreferrer"
								color="primary"
							>
								<FolderOpen />
							</IconButton>
						</PortalTooltip>
						<PortalTooltip title="Copy Airtable URL">
							<IconButton
								onClick={() => {
									navigator.clipboard.writeText(job.url);
								}}
								color="primary"
							>
								<ContentCopy />
							</IconButton>
						</PortalTooltip>
						<PortalTooltip title="Reprocess">
							<IconButton onClick={() => vm.fixOne(job.id)} disabled={vm.fixingAll} color="primary">
								<Build />
							</IconButton>
						</PortalTooltip>
					</Box>
				)}
			</Box>

			{/* Job Details section */}
			<Box sx={sectionSx}>
				<Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
					Job Details
				</Typography>
				<Box sx={gridSx}>
					{/* Row 1: Location */}
					<InfoRow label="Company" value={job.companyName} />
					<InfoRow label="Grower" value={job.growerName} />
					<InfoRow label="Farm" value={job.farmName} />

					{/* Row 2: Field info */}
					<InfoRow label="Field" value={job.fieldName} />
					<InfoRow label="Status" value={job.status} />
					<InfoRow label="Priority" value={job.fieldPriority} />

					{/* Row 3: Dates */}
					<InfoRow label="Date Created" value={new Date(job.dateCreated).toLocaleDateString()} />
					<InfoRow
						label="Sample Date"
						value={job.dateSampled ? new Date(job.dateSampled).toLocaleDateString() : undefined}
					/>
					<InfoRow
						label="Date Shipped"
						value={job.dateShipped ? new Date(job.dateShipped).toLocaleDateString() : undefined}
					/>

					{/* Row 4: Lab & sampling */}
					<InfoRow label="Lab" value={job.labName} />
					<InfoRow label="Test Package" value={job.testPackage} />
					<InfoRow label="Season" value={job.season} />

					{/* Row 5: Metrics */}
					<InfoRow label="Depth" value={job.depth ? String(job.depth) : undefined} />
					<InfoRow label="Density" value={job.density ? String(job.density) : undefined} />
					<InfoRow label="Boundary Acres" value={job.boundaryAcres ? String(job.boundaryAcres) : undefined} />

					{/* Row 6: Submission */}
					<InfoRow label="# Samples" value={job.numberOfSamples ? String(job.numberOfSamples) : undefined} />
					<InfoRow label="Submitter" value={job.submitterName} />
					<InfoRow label="Submitter Email" value={job.submitterEmail} />

					{/* Row 7: Lab results & extras */}
					<InfoRow
						label="Lab Results Sent"
						value={job.dateLabResultsSent ? new Date(job.dateLabResultsSent).toLocaleDateString() : undefined}
					/>
					{job.eventId ? (
						<InfoRow label="Event ID" value={job.eventId} />
					) : (
						<>
							<Box />
							<Box />
						</>
					)}
					{job.jobFlags.length > 0 ? (
						<InfoRow label="Flags" value={job.jobFlags.join(", ")} />
					) : (
						<>
							<Box />
							<Box />
						</>
					)}

					{/* Row 8: Shift */}
					<InfoRow
						label="Shift"
						value={
							vm.shift
								? vm.shift.startDate && vm.shift.endDate
									? `${vm.shift.name} (${vm.shift.startDate.toLocaleDateString()} – ${vm.shift.endDate.toLocaleDateString()})`
									: vm.shift.name
								: undefined
						}
					/>
					<InfoRow label="Robot" value={vm.robot?.name} />
					<Box />
					<Box />
				</Box>
			</Box>

			{/* Submission notes */}
			{job.submissionNotes && (
				<Box sx={sectionSx}>
					<Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
						Submission Notes
					</Typography>
					<Typography variant="body2">{job.submissionNotes}</Typography>
				</Box>
			)}

			{/* Error notes */}
			{job.errorNotes.filter((e) => e.source === "Exit Interview Lambda").length > 0 && (
				<Box sx={{ bgcolor: "rgba(211, 47, 47, 0.06)", borderRadius: 2, p: 2 }}>
					<Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
						<ErrorOutline sx={{ fontSize: 16 }} color="error" />
						<Typography variant="subtitle2" color="error">
							Errors
						</Typography>
					</Stack>
					<Box component="ul" sx={{ m: 0, pl: 2 }}>
						{job.errorNotes
							.filter((e) => e.source === "Exit Interview Lambda")
							.map((error, i) => (
								<Typography key={i} component="li" variant="caption" color="error">
									{error.note}
								</Typography>
							))}
					</Box>
				</Box>
			)}

			{/* Exe Mission details */}
			{vm.exeMission && (
				<Box sx={sectionSx}>
					<Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
						Executed Mission
					</Typography>
					<Box sx={gridSx}>
						<InfoRow label="Type" value={vm.exeMission.type} />
						<InfoRow label="Density" value={vm.exeMission.density ? String(vm.exeMission.density) : undefined} />
						<InfoRow label="Field Name" value={vm.exeMission.fieldName} />

						<InfoRow label="Samples" value={String(vm.exeMission.centroids.length)} />
						<InfoRow label="Cores" value={String(vm.exeMission.cores.length)} />
						<InfoRow label="Zones" value={String(vm.exeMission.sampleZones.length)} />

						<InfoRow label="Rogo Version" value={vm.exeMission.extendedData.rogo_version} />
						<InfoRow label="Event ID" value={vm.exeMission.extendedData.event_id} />
						<InfoRow label="Lab" value={vm.exeMission.extendedData.lab_name} />

						<InfoRow label="Client" value={vm.exeMission.extendedData.client} />
						<InfoRow label="Grower" value={vm.exeMission.extendedData.grower} />
						<InfoRow label="Farm" value={vm.exeMission.extendedData.farm} />

						<InfoRow label="Lab Submittal ID" value={vm.exeMission.extendedData.lab_submittal_id} />
						<InfoRow label="Sample Order Type" value={vm.exeMission.extendedData.sample_order_type} />
						<InfoRow label="Last Modified" value={vm.exeMission.extendedData.last_modified} />
					</Box>
					{vm.exeMission.extendedData.in_field_notes_ops && (
						<Typography variant="body2" sx={{ mt: 1 }}>
							<Typography component="span" variant="body2" color="text.secondary">
								In-Field Notes:{" "}
							</Typography>
							{vm.exeMission.extendedData.in_field_notes_ops}
						</Typography>
					)}
					{vm.exeMission.extendedData.boundary_change_notes && (
						<Typography variant="body2" sx={{ mt: 0.5 }}>
							<Typography component="span" variant="body2" color="text.secondary">
								Boundary Change:{" "}
							</Typography>
							{vm.exeMission.extendedData.boundary_change_type} — {vm.exeMission.extendedData.boundary_change_notes}
						</Typography>
					)}
				</Box>
			)}

			{/* Ops map dialog */}
			<Dialog
				open={vm.mapOpen}
				onClose={() => vm.setMapOpen(false)}
				maxWidth={false}
				slotProps={{ paper: { sx: { width: "90vw", height: "90vh", maxWidth: "90vw" } } }}
			>
				<DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
					Map
					<IconButton size="small" onClick={() => vm.setMapOpen(false)}>
						<Close />
					</IconButton>
				</DialogTitle>
				<DialogContent sx={{ p: 0, overflow: "hidden" }}>
					{vm.mapLoading || vm.missionsLoading ? (
						<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
							<CircularProgress />
						</Box>
					) : (
						<OpsMap
							jobs={[job]}
							intMissions={[vm.intMission].filter((m): m is Mission => !!m)}
							exeMissions={[vm.exeMission].filter((m): m is Mission => !!m)}
							robots={vm.robot ? [vm.robot] : []}
							height="100%"
						/>
					)}
				</DialogContent>
			</Dialog>

			{vm.exeMission && (
				<>
					<SampleDetailsDialog
						open={vm.detailModal === "samples"}
						onClose={() => vm.setDetailModal(null)}
						samples={vm.exeMission.centroids}
						onDownload={vm.downloadSamplesCsv}
					/>
					<CoreDetailsDialog
						open={vm.detailModal === "cores"}
						onClose={() => vm.setDetailModal(null)}
						cores={vm.exeMission.cores}
						onFudge={(coreId) => vm.fudgeCore(vm.selectedJob!.id, coreId)}
						onDeleteSelected={(coreIds) => vm.deleteCores(vm.selectedJob!.id, coreIds)}
						onSetAverageLength={(coreIds) => vm.setAverageCoreLength(vm.selectedJob!.id, coreIds)}
						fudgingCoreId={vm.fudgingCoreId}
						onDownload={vm.downloadCoresCsv}
					/>
				</>
			)}
			{vm.csvDryRunData && (
				<CsvDryRunDialog
					open={vm.csvDryRunModalOpen}
					onClose={() => vm.setCsvDryRunModalOpen(false)}
					csvs={vm.csvDryRunData}
					onDownload={vm.downloadCsvDryRun}
				/>
			)}
		</Stack>
	);
}

function InfoRow({ label, value }: { label: string; value?: string | null }) {
	return (
		<>
			<Typography variant="body2" color="text.secondary" fontWeight={500} noWrap>
				{label}:
			</Typography>
			<Typography
				variant="body2"
				noWrap
				title={value || undefined}
				sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
			>
				{value || "—"}
			</Typography>
		</>
	);
}
